import styled from "@emotion/styled";
import { ReactNode } from "react";
import { sizes } from "../../../constants/spacing";
import { getButtonLoadingStyles } from "../styled/getButtonLoadingStyles";
import { ursaPadding } from "../styled/getButtonPadding";
import { ButtonAppearances, ButtonSizes, LinkAppearances } from "../types";

interface Props {
    appearance: ButtonAppearances | LinkAppearances;
    hasLeftIcon: boolean;
    hasRightIcon: boolean;
    size: ButtonSizes;
    children?: ReactNode;
    clientId?: string;
    isLoading?: boolean;
}

const getMargin = (
    hasLeftIcon: boolean,
    hasRightIcon: boolean,
    appearance: ButtonAppearances | LinkAppearances,
    size: ButtonSizes
) => {
    switch (appearance) {
        // For Lodestar buttons.
        case "borderless":
        case "primary":
        case "secondary":
        case "destructive":
        case "destructive-tertiary":
        case "secondary-destructive":
        case "share":
            // Icons already provide a margin, so the content is only responsible for edges without an icon
            if (hasLeftIcon && hasRightIcon) {
                return { margin: `0` };
            }
            if (hasLeftIcon) {
                return { marginRight: `${sizes.small - 1}px` }; // -1px for border
            }
            if (hasRightIcon) {
                return { marginLeft: `${sizes.small - 1}px` };
            }
            // Proper spacing will be provided by the button padding if there are no icons present
            return { margin: `0` };

        // For Ursa buttons.
        case "primary-filled":
        case "primary-empty":
        case "secondary-filled":
        case "secondary-outlined":
        case "secondary-empty":
        case "canvas-filled":
        case "canvas-empty":
        case "destructive-filled":
        case "destructive-empty":
            // Icons already provide a margin, so the content is only responsible for edges without an icon
            if (hasLeftIcon && hasRightIcon) {
                return { margin: `0 1px` };
            }
            if (hasLeftIcon) {
                // -1px for border, opposite side +1px to account for the -1 on the icon
                return { marginRight: `calc(${ursaPadding[size]} - 1px)`, marginLeft: `1px` };
            }
            if (hasRightIcon) {
                return { marginLeft: `calc(${ursaPadding[size]} - 1px)`, marginRight: `1px` };
            }
            // Proper spacing will be provided by the button padding if there are no icons present
            return { margin: `0` };
    }
};

export const Content = styled("span")<Props>`
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${({ isLoading }) => getButtonLoadingStyles(isLoading)}
    ${({ hasLeftIcon, hasRightIcon, appearance, size }) => getMargin(hasLeftIcon, hasRightIcon, appearance, size)}
`;
